import React, { useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import styles from './index.module.scss'
import { Alert, Button, Checkbox, message, Modal, Pagination, Progress } from 'antd'
import { useIntl } from 'react-intl'
import html2canvas from 'html2canvas'
import { PDFDocument } from 'pdf-lib'
import hmLogo from '../../assets/img/hm_logo.png'
import bstLogo from '../../assets/img/bst.png'
import womalaLogo from '../../assets/img/womala.png'

interface ProductItem {
  main_group?: string
  part_name?: string
  quality_name?: string
  quality_oe?: string
  quality_price?: string | number
  code?: string
  codes?: string[]
  img?: string
  params?: string
  remark?: string
  warranty?: string
}

interface PdfconProps {
  list: ProductItem[]
  setOpen: (open: boolean) => void
  colSelect: string[]
  title?: string
  inlogo: string
  dph?: string
  partnameData: Record<string, Record<string, string>>
  partlangkey: string
  shownum: number
}

interface CardImage {
  url: string
  image?: HTMLImageElement
  width: number
  height: number
}

type ExportMode = 'single' | 'multiple'
type ExportFileStatus = 'waiting' | 'exporting' | 'completed' | 'failed' | 'terminated'

interface ExportFileItem {
  id: string
  index: number
  filename: string
  start: number
  end: number
  checked: boolean
  progress: number
  status: ExportFileStatus
}

const A4_WIDTH = 210
const A4_HEIGHT = 297
const PAGE_MARGIN = 10
const CONTENT_WIDTH = A4_WIDTH - PAGE_MARGIN * 2
const EXPORT_WIDTH = 1000
const CANVAS_SCALE = 1.1
const JPEG_QUALITY = 1
const HEADER_HEIGHT_PX = 150
const CARD_GAP_PX = 10
const PDF_URL_REVOKE_DELAY = 1000
const MULTI_PDF_URL_REVOKE_DELAY = 1000
const SINGLE_PDF_URL_REVOKE_DELAY = 1000
const MEMORY_SETTLE_TICKS = 3
const EXPORT_PROGRESS_UPDATE_INTERVAL = 200
const EXPORT_INTERACTIVE_PAUSE = 16
const INDEPENDENT_CARD_BATCH_SIZE = 80
const LARGE_DATA_THRESHOLD = 200
const MULTI_EXPORT_SIZE = 200
const PREVIEW_PAGE_SIZE = 24
const STYLE_ONE_COLUMNS = 2
const STYLE_ONE_HEADER_HEIGHT_PX = 170
const STYLE_ONE_BATCH_SIZE = 40

const HM_HOSTS = ['webtest.maiqipei.com', 'gzhmqy.maiqipei.com', 'gzhmqy.com', '300']
const BST_HOSTS = ['bmtsr.maiqipei.com', 'bmtsr.xiaomaiqipei.com']
const SEN_HOSTS = ['senp.maiqipei.com', 'senp.xiaomaiqipei.com']
const WOMALA_HOSTS = ['womala.maiqipei.com', 'womala.xiaomaiqipei.com', 'www.womalacarpart.com', 'womalacarpart.com']

const includesHost = (origin: string, hosts: string[]) => hosts.some((host) => origin.includes(host))
const getTitleText = (title?: string) => title?.split('>').join(' ') || ''
const sanitizeFileName = (name: string) => name.replace(/[\\/:*?"<>|]/g, '-')
const getDownloadName = (title?: string) => {
  const name = title?.split('>').slice(0, 3).join('-').trim() || 'products'
  return `${sanitizeFileName(name)}.pdf`
}
const getDateText = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const date = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${date}`
}
const getExportTitle = (title?: string) => {
  const name = title?.split('>').slice(0, 3).join('-').trim() || 'products'
  return sanitizeFileName(name)
}
const getSingleExportFileName = (title: string | undefined) => {
  return `${getDateText()} ${getExportTitle(title)}.pdf`
}
const getMultiExportFileName = (title: string | undefined, index: number) => {
  return `${getDateText()} ${getExportTitle(title)}-${index}.pdf`
}
const Pdfcon: React.FC<PdfconProps> = (props) => {
  const intl = useIntl()
  const { list, setOpen, colSelect, title, inlogo, dph, partnameData, partlangkey, shownum } = props

  const [loading, setLoading] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)
  const [processedCount, setProcessedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [previewPage, setPreviewPage] = useState(1)
  const [exportModeOpen, setExportModeOpen] = useState(false)
  const [exportProgressOpen, setExportProgressOpen] = useState(false)
  const [exportMode, setExportMode] = useState<ExportMode>('single')
  const [exportFiles, setExportFiles] = useState<ExportFileItem[]>([])
  const [multiExporting, setMultiExporting] = useState(false)
  const [multiExportCompleted, setMultiExportCompleted] = useState(false)
  const cancelledRef = useRef(false)
  const multiCancelledRef = useRef(false)
  const totalCountRef = useRef(0)
  const lastExportProgressUpdateRef = useRef(0)
  const exportProgressHandlerRef = useRef<null | ((percent: number, processed: number, total: number) => void)>(null)
  const cleanupTasksRef = useRef(new Set<() => void>())
  const objectUrlsRef = useRef(new Set<string>())
  const exportStartTimerRef = useRef<number | null>(null)
  const mountedRef = useRef(true)

  const origin = window.location.origin
  const ishm = includesHost(origin, HM_HOSTS)
  const isdp = origin.includes('dpatp')
  const isSen = includesHost(origin, SEN_HOSTS)
  const isWomala = includesHost(origin, WOMALA_HOSTS)
  const logo = ishm ? hmLogo : includesHost(origin, BST_HOSTS) ? bstLogo : isWomala ? womalaLogo : inlogo
  const titleText = getTitleText(title)
  const jsPDF = (window as any).jspdf?.jsPDF
  // HM 页面图片较多且需要放大查看，导出时提高 Canvas 采样倍率以改善 PDF 清晰度。
  const exportCanvasScale = ishm ? 1.6 : CANVAS_SCALE
  const usePaginatedPreview = list.length > LARGE_DATA_THRESHOLD
  const storedLang = window.localStorage.getItem('lang')
  const lang = storedLang === 'zh' ? 'cn' : storedLang
  const getTranslatedPartName = (item: ProductItem) => {
    if(!item.part_name || !colSelect.includes('part_name')) return ''
    return partnameData[item.part_name]?.[partlangkey] || ''
  }

  const classList: any = useMemo(() => {
    let _result: any = {}
    let result: any = []
    // hm要求按照编码首位排序
    if(ishm) return list
    list.forEach((item: any) => {
      if(_result[item.main_group]) {
        _result[item.main_group].push(item)
      }else  {
        _result[item.main_group] = [item]
      }
    })
    let num = 4
    if(isdp) num = 3
    Object.keys(_result).forEach((key) => {
      let extraLen = num - (_result[key].length % num)
      _result[key] = [..._result[key], ...Array.from({ length: extraLen !== num ? extraLen: 0 }, () => ({main_group: key}))]
      result = [...result, ..._result[key]]
    })
    return result
  },[isdp, ishm, list])

  const cardItems = shownum === 2 ? classList : list
  const previewItems = useMemo(() => {
    if(!usePaginatedPreview) return cardItems
    const start = (previewPage - 1) * PREVIEW_PAGE_SIZE
    return cardItems.slice(start, start + PREVIEW_PAGE_SIZE)
  }, [cardItems, previewPage, usePaginatedPreview])

  useEffect(() => {
    setPreviewPage(1)
  }, [list, shownum])

  useEffect(() => {
    const cleanupTasks = cleanupTasksRef.current
    return () => {
      mountedRef.current = false
      cancelledRef.current = true
      multiCancelledRef.current = true
      if(exportStartTimerRef.current !== null) {
        window.clearTimeout(exportStartTimerRef.current)
        exportStartTimerRef.current = null
      }
      exportProgressHandlerRef.current = null
      cleanupTasks.forEach((cleanup) => cleanup())
      cleanupTasks.clear()
      revokeManagedObjectUrls()
    }
  }, [])

  const revokeManagedObjectUrls = () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current.clear()
  }

  const ensureExportActive = () => {
    if(cancelledRef.current) throw new Error('PDF_EXPORT_CANCELLED')
  }

  const updateExportProgress = (percent: number, processed?: number) => {
    if(cancelledRef.current) return
    if(exportProgressHandlerRef.current) {
      exportProgressHandlerRef.current(percent, processed || 0, totalCountRef.current)
      return
    }
    setProgress(percent)
    if(processed !== undefined) setProcessedCount(Math.min(processed, totalCountRef.current))
  }

  const registerCleanup = (cleanupAction: () => void) => {
    let cleaned = false
    const cleanup = () => {
      if(cleaned) return
      cleaned = true
      cleanupTasksRef.current.delete(cleanup)
      cleanupAction()
    }
    cleanupTasksRef.current.add(cleanup)
    return cleanup
  }

  const createManagedRoot = (rootContainer: HTMLElement, ownerContainer = rootContainer) => {
    const root = createRoot(rootContainer)
    const cleanup = registerCleanup(() => {
      root.unmount()
      ownerContainer.remove()
    })
    return { root, cleanup }
  }

  const renderStyleThreeCard = (item: ProductItem, index: number) => (
    <div className={styles['product-card-3']} key={`${previewPage}-${index}`}>
      <div className={styles['img-wrap'] + ' flex-c-c'}>
        {colSelect.includes('img') && item.img ? (
          <img alt="" className={styles['img-1']} src={item.img} />
        ) : null}
      </div>
      <div className={styles['product-card-3-content'] + ' p10'}>
        {colSelect.includes('part_name') ? <h3>{item.part_name}</h3> : null}
        {partlangkey !== lang ? <h3>{getTranslatedPartName(item)}</h3> : null}
        {colSelect.includes('quality_name') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>{intl.formatMessage({ id: '品牌' })}:</span>
            <span className={styles['prop-value']}>{item.quality_name}</span>
          </div>
        ) : null}
        {colSelect.includes('quality_oe') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>{intl.formatMessage({ id: 'OE' })}:</span>
            <span className={styles['prop-value']}>{item.quality_oe}</span>
          </div>
        ) : null}
        {colSelect.includes('params') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>
              {isSen ? intl.formatMessage({ id: '车型' }) : intl.formatMessage({ id: '规格' })}:
            </span>
            <span className={styles['prop-value']} style={{ wordBreak: 'break-all' }}>{item.params}</span>
          </div>
        ) : null}
        {colSelect.includes('remark') ? (
          <div className={styles['product-props']}>
            <span style={{ width: 50 }} className={styles['prop-label']}>
              {isSen ? intl.formatMessage({ id: '规格' }) : intl.formatMessage({ id: '备注' })}:
            </span>
            <span className={styles['prop-value']}>{item.remark}</span>
          </div>
        ) : null}
        <div className="flex-s-c" style={{ fontSize: 15, fontWeight: 500 }}>
          <div>{isSen ? intl.formatMessage({ id: '自编码' }) : intl.formatMessage({ id: '编码' })}:</div>
          {colSelect.includes('code') && item.codes?.length
            ? item.codes.map((code, codeIndex) => <div key={codeIndex}>{code}</div>)
            : null}
        </div>
      </div>
    </div>
  )

  const renderStyleTwoCard = (item: ProductItem, index: number) => (
    <div
      className={`${styles['product-card-1']} ${isdp ? styles['product-card-1-dp'] : ''}`}
      key={`${previewPage}-${index}`}
    >
      <div className="flex plr10" style={{ justifyContent: 'space-between' }}>
        <div>
          {item.part_name ? <div className="p-color">{item.main_group || ''}</div> : null}
          <div>
            {item.part_name ? dph : ''} {colSelect.includes('part_name') && item.part_name ? item.part_name : ''}
          </div>
          {partlangkey !== lang ? <div>{getTranslatedPartName(item)}</div> : null}
          <div>
            {colSelect.includes('code') && item.codes?.length
              ? item.codes.map((code, codeIndex) => <div key={codeIndex}>{code}</div>)
              : null}
          </div>
        </div>
        <img alt="" className={styles['logo-1'] + ' ml10'} src={logo} />
      </div>
      <div className="flex-c-c">
        {colSelect.includes('img') && item.img ? (
          <img alt="" className={styles['img-1']} src={item.img} />
        ) : null}
      </div>
    </div>
  )

  const renderStyleOneCard = (item: ProductItem, index: number) => (
    <div className={styles['product-card']} key={`${previewPage}-${index}`}>
      {colSelect.includes('part_name') ? <h3>{item.part_name}</h3> : null}
      {partlangkey !== lang ? <h3>{getTranslatedPartName(item)}</h3> : null}
      <div style={{ borderTop: '2px solid #ff7f00' }} className="flex-b-c">
        <div>
          {item.part_name && item.main_group ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '分类' })}:</span>
              <span className={styles['prop-value'] + ' p-color'} style={{ color: '#096BED' }}>{item.main_group}</span>
            </div>
          ) : null}
          {colSelect.includes('quality_oe') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '原厂OE' })}:</span>
              <span className={styles['prop-value']}>{item.quality_oe}</span>
            </div>
          ) : null}
          {colSelect.includes('code') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '产品编码' })}:</span>
              <span className={styles['prop-value']}>{item.code}</span>
            </div>
          ) : null}
          {colSelect.includes('warranty') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '质保时间' })}:</span>
              <span className={styles['prop-value']}>{item.warranty}</span>
            </div>
          ) : null}
          {colSelect.includes('quality_name') && colSelect.includes('quality_price') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '销售价格' })}:</span>
              <span className={styles['prop-value']}>{item.quality_name}: ¥{item.quality_price}</span>
            </div>
          ) : null}
          {colSelect.includes('params') ? (
            <div className={styles['product-props']}>
              <span className={styles['prop-label']}>{intl.formatMessage({ id: '产品参数' })}:</span>
              <span className={styles['prop-value']}>{item.params}</span>
            </div>
          ) : null}
        </div>
        {colSelect.includes('img') ? (
          <div className={styles['product-img']}>
            <img alt="" src={item.img} />
          </div>
        ) : null}
      </div>
    </div>
  )

  const renderCard = shownum === 1 ? renderStyleOneCard : shownum === 2 ? renderStyleTwoCard : renderStyleThreeCard

  const buildExportFiles = (items: ProductItem[]) => {
    const fileCount = Math.ceil(items.length / MULTI_EXPORT_SIZE)
    return Array.from({ length: fileCount }, (_, fileIndex) => {
      const index = fileIndex + 1
      const start = fileIndex * MULTI_EXPORT_SIZE
      return {
        id: `pdf-${index}`,
        index,
        filename: fileCount === 1 ? getSingleExportFileName(title) : getMultiExportFileName(title, index),
        start,
        end: Math.min(start + MULTI_EXPORT_SIZE, items.length),
        checked: true,
        progress: 0,
        status: 'waiting' as ExportFileStatus,
      }
    })
  }

  const updateExportFile = (id: string, patch: Partial<ExportFileItem>) => {
    if(!mountedRef.current) return
    setExportFiles((files) => files.map((file) => file.id === id ? { ...file, ...patch } : file))
  }

  const checkedExportFileCount = exportFiles.filter((file) => file.checked).length
  const allExportFilesChecked = exportFiles.length > 0 && checkedExportFileCount === exportFiles.length
  const someExportFilesChecked = checkedExportFileCount > 0 && checkedExportFileCount < exportFiles.length

  const toggleAllExportFiles = (checked: boolean) => {
    if(!mountedRef.current) return
    setExportFiles((files) => files.map((file) => ({ ...file, checked })))
  }

  const downloadFile = (url: string, filename = getDownloadName(title), showSuccessMessage = true) => {
    ensureExportActive()
    const a = document.createElement('a')
    a.download = filename
    a.href = url
    document.body.appendChild(a)
    a.click()
    a.remove()
    if(showSuccessMessage) message.success(intl.formatMessage({ id: '导出成功' }))
  }

  const appendPdfData = async (targetPdf: PDFDocument, pdfData: ArrayBuffer) => {
    ensureExportActive()
    const sourcePdf = await PDFDocument.load(pdfData)
    ensureExportActive()
    const copiedPages = await targetPdf.copyPages(sourcePdf, sourcePdf.getPageIndices())
    copiedPages.forEach((page) => targetPdf.addPage(page))
  }

  const canvasToBlob = (canvas: HTMLCanvasElement) => {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if(blob) {
          resolve(blob)
        }else {
          reject(new Error('Canvas blob is unavailable'))
        }
      }, 'image/jpeg', JPEG_QUALITY)
    })
  }

  const imageFromObjectUrl = (url: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error('Canvas image load failed'))
      image.src = url
    })
  }

  const cleanupCardImage = (item: CardImage) => {
    URL.revokeObjectURL(item.url)
    if(item.image) item.image.src = ''
  }

  const cleanupCardImages = (items: CardImage[]) => {
    items.forEach(cleanupCardImage)
    items.length = 0
  }

  async function containerToPdfData(container: HTMLElement, scale = exportCanvasScale) {
    if(!jsPDF) throw new Error('jsPDF is unavailable')

    const canvas = await html2canvas(container, {
      allowTaint: true,
      height: container.scrollHeight,
      width: EXPORT_WIDTH,
      scale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
    })
    try {
      if(cancelledRef.current) ensureExportActive()

      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageContentHeight = A4_HEIGHT - PAGE_MARGIN * 2
      const pageImageHeight = Math.floor(pageContentHeight * canvas.width / CONTENT_WIDTH)
      let renderedHeight = 0

      while(renderedHeight < canvas.height) {
        const currentPageHeight = Math.min(pageImageHeight, canvas.height - renderedHeight)
        const pageCanvas = document.createElement('canvas')
        const pageContext = pageCanvas.getContext('2d')
        if(!pageContext) throw new Error('Canvas context is unavailable')

        try {
          pageCanvas.width = canvas.width
          pageCanvas.height = currentPageHeight
          pageContext.drawImage(
            canvas,
            0,
            renderedHeight,
            canvas.width,
            currentPageHeight,
            0,
            0,
            canvas.width,
            currentPageHeight,
          )
          pdf.addImage(pageCanvas, 'JPEG', PAGE_MARGIN, PAGE_MARGIN, CONTENT_WIDTH, Math.min(pageContentHeight, CONTENT_WIDTH * currentPageHeight / pageCanvas.width))
        }finally {
          pageCanvas.width = 0
          pageCanvas.height = 0
        }
        renderedHeight += pageImageHeight
        if(renderedHeight < canvas.height) pdf.addPage()
      }

      return pdf.output('arraybuffer') as ArrayBuffer
    }finally {
      canvas.width = 0
      canvas.height = 0
    }
  }

  async function waitForContainerAssets(container: HTMLElement) {
    const images = Array.from(container.querySelectorAll('img'))
    await Promise.all(images.map((image) => {
      if(image.complete) return Promise.resolve()
      return new Promise<void>((resolve) => {
        let timeout = 0
        const cleanup = registerCleanup(() => {
          window.clearTimeout(timeout)
          image.removeEventListener('load', cleanup)
          image.removeEventListener('error', cleanup)
          resolve()
        })
        timeout = window.setTimeout(cleanup, 10000)
        image.addEventListener('load', cleanup, { once: true })
        image.addEventListener('error', cleanup, { once: true })
      })
    }))
    await document.fonts?.ready
    ensureExportActive()
    // 强制浏览器完成布局计算，避免依赖后台标签页会暂停的 requestAnimationFrame。
    void container.offsetHeight
  }

  const yieldToBrowser = (delay = 0) => {
    // 后台标签页的 setTimeout 会被强制降频；后台导出时直接继续任务链。
    if(document.hidden) return Promise.resolve()

    return new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.setTimeout(resolve, delay)
      })
    })
  }

  const waitForBrowserIdle = () => {
    if(document.hidden || !('requestIdleCallback' in window)) return yieldToBrowser()

    return new Promise<void>((resolve) => {
      window.requestIdleCallback(() => resolve(), { timeout: 1000 })
    })
  }

  const waitForBrowserMemorySettle = async () => {
    for(let i = 0; i < MEMORY_SETTLE_TICKS; i++) {
      await yieldToBrowser()
      await waitForBrowserIdle()
    }
    ;(window as any).gc?.()
    await yieldToBrowser()
  }

  function createLargeExportContainer() {
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.left = '-10000px'
    container.style.top = '0'
    container.style.width = `${EXPORT_WIDTH}px`
    container.style.padding = '40px 0 0'
    container.style.boxSizing = 'border-box'
    container.style.backgroundColor = '#FFFFFF'

    const logoElement = document.createElement('img')
    logoElement.src = logo
    logoElement.style.display = 'block'
    logoElement.style.height = '60px'
    logoElement.style.marginBottom = '25px'
    container.appendChild(logoElement)

    const titleElement = document.createElement('div')
    titleElement.innerText = titleText
    titleElement.style.display = 'inline-block'
    titleElement.style.padding = '5px 15px'
    titleElement.style.borderRadius = '8px'
    titleElement.style.backgroundColor = shownum === 3 ? '#333333' : '#1890ff'
    titleElement.style.color = '#FFFFFF'
    titleElement.style.fontSize = '18px'
    titleElement.style.fontWeight = '500'
    container.appendChild(titleElement)

    const cardsHost = document.createElement('div')
    cardsHost.className = styles['products']
    cardsHost.style.marginTop = '20px'
    container.appendChild(cardsHost)
    document.body.appendChild(container)

    return { container, cardsHost }
  }

  async function appendCardPagePDFs(targetPdf: PDFDocument, items: ProductItem[]) {
    const columns = shownum === 2 && isdp ? 3 : 4
    const rowsPerBatch = shownum === 2 ? (isdp ? 3 : 5) : 3
    const batchSize = columns * rowsPerBatch
    const batchCount = Math.ceil(items.length / batchSize)

    for(let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const { container, cardsHost } = createLargeExportContainer()
      const { root, cleanup } = createManagedRoot(cardsHost, container)

      try {
        const batchItems = items.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize)
        flushSync(() => root.render(<>{batchItems.map(renderCard)}</>))
        await waitForContainerAssets(container)
        const pdfData = await containerToPdfData(container)
        await appendPdfData(targetPdf, pdfData)
        const processed = Math.min((batchIndex + 1) * batchSize, items.length)
        updateExportProgress(Math.round((processed / items.length) * 90), processed)
        await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
        ensureExportActive()
      }finally {
        cleanup()
      }
    }
  }

  async function generateIndependentCardPDFData(items: ProductItem[]) {
    if(!items.length) return new ArrayBuffer(0)
    if(!jsPDF) throw new Error('jsPDF is unavailable')

    const columns = shownum === 1 ? STYLE_ONE_COLUMNS : shownum === 2 && isdp ? 3 : 4
    const batchSize = INDEPENDENT_CARD_BATCH_SIZE
    const batchCount = Math.ceil(items.length / batchSize)
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pxToMm = CONTENT_WIDTH / EXPORT_WIDTH
    const cardGap = CARD_GAP_PX * pxToMm
    const startY = PAGE_MARGIN + HEADER_HEIGHT_PX * pxToMm
    const pageBottom = A4_HEIGHT - PAGE_MARGIN
    const titleBackgroundColor = shownum === 3 ? '#333333' : '#1890ff'
    let logoDataUrl = ''
    const titleDataUrl = textToDataUrl(titleText, titleBackgroundColor)
    let headerAdded = false
    let currentY = startY

    try {
      logoDataUrl = await imageToDataUrl(logo)
      ensureExportActive()
    }catch(error) {
      if((error as Error).message === 'PDF_EXPORT_CANCELLED') throw error
      console.warn('PDF logo could not be loaded', error)
      logoDataUrl = ''
    }

    const addHeader = () => {
      if(headerAdded) return

      if(logoDataUrl) {
        const logoProps = pdf.getImageProperties(logoDataUrl)
        const logoW = 100 * pxToMm
        const logoH = logoProps.height ? logoW * (logoProps.height / logoProps.width) : logoW
        pdf.addImage(logoDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 40 * pxToMm, logoW, logoH)
      }
      if(titleDataUrl) {
        const titleProps = pdf.getImageProperties(titleDataUrl)
        const titleW = Math.min(titleProps.width * pxToMm, CONTENT_WIDTH)
        const titleH = titleProps.width ? titleW * (titleProps.height / titleProps.width) : 0
        pdf.addImage(titleDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 108 * pxToMm, titleW, titleH)
      }

      headerAdded = true
    }

    addHeader()

    for(let batchIndex = 0; batchIndex < batchCount; batchIndex++) {
      const { container, cardsHost } = createLargeExportContainer()
      const { root, cleanup } = createManagedRoot(cardsHost, container)

      try {
        const batchStart = batchIndex * batchSize
        const batchItems = items.slice(batchStart, batchStart + batchSize)
        if(shownum === 1) cardsHost.className = `${styles['products']} ${styles['products-style-one']}`
        flushSync(() => root.render(<>{batchItems.map(renderCard)}</>))
        await waitForContainerAssets(container)
        const children = Array.from(cardsHost.children) as HTMLElement[]

        for(let i = 0; i < children.length; i += columns) {
          const rowElements = children.slice(i, i + columns)
          const rowItems: CardImage[] = []
          try {
            for(const element of rowElements) {
              rowItems.push(await elementToImage(element))
            }
            ensureExportActive()

            const rowHeights = rowItems.map((item) => item.height * pxToMm)
            const rowHeight = Math.max(...rowHeights, 0)
            const availablePageHeight = pageBottom - startY
            const rowScale = rowHeight > availablePageHeight ? availablePageHeight / rowHeight : 1
            const scaledRowHeight = rowHeight * rowScale

            if(currentY !== startY && currentY + scaledRowHeight > pageBottom) {
              pdf.addPage()
              headerAdded = false
              addHeader()
              currentY = startY
            }

            for(let col = 0; col < rowItems.length; col++) {
              const item = rowItems[col]
              const itemWidth = item.width * pxToMm * rowScale
              const itemHeight = item.height * pxToMm * rowScale
              const x = PAGE_MARGIN + col * ((item.width * pxToMm) + cardGap)
              pdf.addImage(item.image || item.url, 'JPEG', x, currentY, itemWidth, itemHeight)
            }

            currentY += scaledRowHeight + cardGap
            const completed = Math.min(batchStart + i + columns, items.length)
            updateExportProgress(Math.round((completed / items.length) * 90), completed)
            await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
          }finally {
            cleanupCardImages(rowItems)
          }
        }

        await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
        ensureExportActive()
      }finally {
        cleanup()
      }
    }

    return pdf.output('arraybuffer') as ArrayBuffer
  }

  function createStyleOneExportContainer() {
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.left = '-10000px'
    container.style.top = '0'
    container.style.width = `${EXPORT_WIDTH}px`
    container.style.backgroundColor = '#FFFFFF'
    container.style.boxSizing = 'border-box'
    container.style.paddingTop = `${STYLE_ONE_HEADER_HEIGHT_PX}px`

    const logoElement = document.createElement('img')
    logoElement.src = logo
    logoElement.style.height = '60px'
    logoElement.className = styles['page-logo']
    container.appendChild(logoElement)

    const titleElement = document.createElement('div')
    titleElement.innerText = titleText
    titleElement.className = styles['sp']
    container.appendChild(titleElement)

    const cardsHost = document.createElement('div')
    cardsHost.className = `${styles['products']} ${styles['products-style-one']}`
    cardsHost.style.marginTop = '0'
    container.appendChild(cardsHost)

    document.body.appendChild(container)
    return { container, cardsHost }
  }

  async function appendStyleOnePagedPDFs(targetPdf: PDFDocument, items: ProductItem[]) {
    if(!items.length) return

    const pageContentHeightPx = Math.floor((A4_HEIGHT - PAGE_MARGIN * 2) * EXPORT_WIDTH / CONTENT_WIDTH)
    const availableRowsHeightPx = pageContentHeightPx - STYLE_ONE_HEADER_HEIGHT_PX
    const { container: measureContainer, cardsHost: measureCardsHost } = createStyleOneExportContainer()
    const { root: measureRoot, cleanup: cleanupMeasure } = createManagedRoot(measureCardsHost, measureContainer)
    let currentPage = createStyleOneExportContainer()
    let currentPageHeight = 0
    let currentPageItemCount = 0
    let processed = 0

    const appendCurrentPage = async () => {
      if(!currentPageItemCount) return

      await waitForContainerAssets(currentPage.container)
      const pdfData = await containerToPdfData(currentPage.container)
      await appendPdfData(targetPdf, pdfData)
      processed += currentPageItemCount
      updateExportProgress(Math.round((processed / items.length) * 90), processed)
      currentPage.container.remove()
      currentPage = createStyleOneExportContainer()
      currentPageHeight = 0
      currentPageItemCount = 0
      await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
      ensureExportActive()
    }

    try {
      for(let batchStart = 0; batchStart < items.length; batchStart += STYLE_ONE_BATCH_SIZE) {
        const batchItems = items.slice(batchStart, batchStart + STYLE_ONE_BATCH_SIZE)
        flushSync(() => measureRoot.render(<>{batchItems.map(renderStyleOneCard)}</>))
        await waitForContainerAssets(measureContainer)
        const cards = Array.from(measureCardsHost.children) as HTMLElement[]

        for(let i = 0; i < batchItems.length; i += STYLE_ONE_COLUMNS) {
          const rowCards = cards.slice(i, i + STYLE_ONE_COLUMNS)
          const rowHeight = Math.max(...rowCards.map((card) => Math.ceil(card.getBoundingClientRect().height)), 0)
          const rowHeightWithGap = currentPageItemCount ? rowHeight + CARD_GAP_PX : rowHeight
          const itemStartIndex = batchStart + i

          if(rowHeight > availableRowsHeightPx) {
            console.warn('PDF style one card row exceeds one page height', { startIndex: itemStartIndex, rowHeight, availableRowsHeightPx })
          }

          if(currentPageItemCount && currentPageHeight + rowHeightWithGap > availableRowsHeightPx) {
            await appendCurrentPage()
          }

          for(const card of rowCards) {
            currentPage.cardsHost.appendChild(card.cloneNode(true))
          }
          currentPageHeight += currentPageItemCount ? rowHeight + CARD_GAP_PX : rowHeight
          currentPageItemCount += rowCards.length
        }

        ensureExportActive()
      }

      await appendCurrentPage()
    }finally {
      currentPage.container.remove()
      cleanupMeasure()
    }
  }

  async function elementToImage(element: HTMLElement) {
    const rect = element.getBoundingClientRect()
    const width = Math.ceil(rect.width)
    const height = Math.ceil(rect.height)
    const canvas = await html2canvas(element, {
      allowTaint: true,
      height,
      width,
      scale: exportCanvasScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
    })
    try {
      if(cancelledRef.current) ensureExportActive()

      const objectUrl = URL.createObjectURL(await canvasToBlob(canvas))
      let imageElement: HTMLImageElement
      try {
        imageElement = await imageFromObjectUrl(objectUrl)
        ensureExportActive()
      }catch(error) {
        URL.revokeObjectURL(objectUrl)
        throw error
      }
      return {
        url: objectUrl,
        image: imageElement,
        width,
        height,
      }
    }finally {
      canvas.width = 0
      canvas.height = 0
    }
  }

  async function imageToDataUrl(src: string) {
    if(!src) return ''

    return new Promise<string>((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        try {
          canvas.width = img.naturalWidth || img.width
          canvas.height = img.naturalHeight || img.height
          const context = canvas.getContext('2d')
          if(!context) {
            reject(new Error('Canvas context is unavailable'))
            return
          }
          context.drawImage(img, 0, 0)
          resolve(canvas.toDataURL('image/png'))
        }finally {
          canvas.width = 0
          canvas.height = 0
        }
      }
      img.onerror = () => reject(new Error(`Image load failed: ${src}`))
      img.src = src
    })
  }

  function textToDataUrl(text: string, backgroundColor = '#1890ff') {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if(!ctx) return ''

    const fontSize = 18
    const paddingX = 8
    const paddingY = 6
    ctx.font = `500 ${fontSize}px Arial, "Microsoft YaHei", sans-serif`
    const metrics = ctx.measureText(text)
    const width = Math.ceil(metrics.width + paddingX * 2)
    const height = Math.ceil(fontSize + paddingY * 2)
    canvas.width = width
    canvas.height = height

    const radius = 6
    ctx.clearRect(0, 0, width, height)
    ctx.font = `500 ${fontSize}px Arial, "Microsoft YaHei", sans-serif`
    ctx.fillStyle = backgroundColor
    ctx.beginPath()
    ctx.moveTo(radius, 0)
    ctx.lineTo(width - radius, 0)
    ctx.quadraticCurveTo(width, 0, width, radius)
    ctx.lineTo(width, height - radius)
    ctx.quadraticCurveTo(width, height, width - radius, height)
    ctx.lineTo(radius, height)
    ctx.quadraticCurveTo(0, height, 0, height - radius)
    ctx.lineTo(0, radius)
    ctx.quadraticCurveTo(0, 0, radius, 0)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = '#ffffff'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, paddingX, height / 2 + 1)
    const dataUrl = canvas.toDataURL('image/png')
    canvas.width = 0
    canvas.height = 0
    return dataUrl
  }

  async function generateCardImagePDF(
    children: HTMLElement[],
    cols: number,
    titleBackgroundColor = '#1890ff',
    onProgress: (completed: number, total: number) => void = (completed, total) => {
      updateExportProgress(Math.round((completed / total) * 85), completed)
    },
  ) {
    if(!jsPDF) throw new Error('jsPDF is unavailable')

    const pdf = new jsPDF('p', 'mm', 'a4')
    const pxToMm = CONTENT_WIDTH / EXPORT_WIDTH
    const cardGap = CARD_GAP_PX * pxToMm
    const startY = PAGE_MARGIN + HEADER_HEIGHT_PX * pxToMm
    const pageBottom = A4_HEIGHT - PAGE_MARGIN
    let logoDataUrl = ''
    const titleDataUrl = textToDataUrl(titleText, titleBackgroundColor)
    let headerAdded = false

    try {
      logoDataUrl = await imageToDataUrl(logo)
      ensureExportActive()
    }catch(error) {
      if((error as Error).message === 'PDF_EXPORT_CANCELLED') throw error
      console.warn('PDF logo could not be loaded', error)
      logoDataUrl = ''
    }

    const addHeader = () => {
      if(headerAdded) return

      if(logoDataUrl) {
        const logoProps = pdf.getImageProperties(logoDataUrl)
        const logoW = 100 * pxToMm
        const logoH = logoProps.height ? logoW * (logoProps.height / logoProps.width) : logoW
        pdf.addImage(logoDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 40 * pxToMm, logoW, logoH)
      }
      if(titleDataUrl) {
        const titleProps = pdf.getImageProperties(titleDataUrl)
        const titleW = Math.min(titleProps.width * pxToMm, CONTENT_WIDTH)
        const titleH = titleProps.width ? titleW * (titleProps.height / titleProps.width) : 0
        pdf.addImage(titleDataUrl, 'PNG', PAGE_MARGIN, PAGE_MARGIN + 108 * pxToMm, titleW, titleH)
      }

      headerAdded = true
    }

    addHeader()
    let currentY = startY

    for(let i = 0; i < children.length; i += cols) {
      const rowElements = children.slice(i, i + cols)
      const rowItems: CardImage[] = []
      try {
        for(const element of rowElements) {
          rowItems.push(await elementToImage(element))
        }
        ensureExportActive()
        const rowHeights = rowItems.map((item) => item.height * pxToMm)
        const rowHeight = Math.max(...rowHeights, 0)
        const availablePageHeight = pageBottom - startY
        const rowScale = rowHeight > availablePageHeight ? availablePageHeight / rowHeight : 1
        const scaledRowHeight = rowHeight * rowScale

        if(currentY !== startY && currentY + scaledRowHeight > pageBottom) {
          pdf.addPage()
          headerAdded = false
          addHeader()
          currentY = startY
        }

        for(let col = 0; col < rowItems.length; col++) {
          const item = rowItems[col]
          const itemWidth = item.width * pxToMm * rowScale
          const itemHeight = item.height * pxToMm * rowScale
          const x = PAGE_MARGIN + col * ((item.width * pxToMm) + cardGap)
          pdf.addImage(item.image || item.url, 'JPEG', x, currentY, itemWidth, itemHeight)
        }
        currentY += scaledRowHeight + cardGap
        onProgress(Math.min(i + cols, children.length), children.length)
        await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
      }finally {
        cleanupCardImages(rowItems)
      }
    }

    return pdf.output('arraybuffer') as ArrayBuffer
  }

  async function generatePDF(
    id: string,
    options: {
      items?: ProductItem[]
      filename?: string
      forceMergedCardExport?: boolean
      useIndependentCardExport?: boolean
      showSuccessMessage?: boolean
      preserveProgressState?: boolean
      waitForObjectUrlRevoke?: boolean
      objectUrlRevokeDelay?: number
    } = {},
  ) {
    const container = document.getElementById(id)
    const exportItems = options.items || cardItems
    if(!exportItems.length || (!container && !options.items)) {
      message.warning(intl.formatMessage({ id: '请先搜索导出数据' }))
      return false
    }

    setLoading(true)
    cancelledRef.current = false
    const exportTotal = exportItems.length
    totalCountRef.current = exportTotal
    if(!options.preserveProgressState) {
      setTotalCount(exportTotal)
      setProcessedCount(0)
      setProgress(1)
    }
    let objectUrl = ''
    let tempContainer: HTMLDivElement | null = null
    let cleanupTempContainer: (() => void) | null = null

    try {
      const children = container ? Array.from(container.children) as HTMLElement[] : []
      const enableIndependentCardExport = !!options.useIndependentCardExport && !options.forceMergedCardExport

      if(enableIndependentCardExport && !usePaginatedPreview && !options.items) {
        let pdfData: ArrayBuffer | null = await generateCardImagePDF(
          children,
          4,
          '#1890ff',
        )
        objectUrl = URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }))
        pdfData = null
        objectUrlsRef.current.add(objectUrl)
        updateExportProgress(100, exportTotal)
        downloadFile(objectUrl, options.filename, options.showSuccessMessage !== false)
        return true
      }

      if(enableIndependentCardExport) {
        let pdfData: ArrayBuffer | null = await generateIndependentCardPDFData(exportItems)
        objectUrl = URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }))
        pdfData = null
        objectUrlsRef.current.add(objectUrl)
        updateExportProgress(100, exportTotal)
        downloadFile(objectUrl, options.filename, options.showSuccessMessage !== false)
        return true
      }

      const mergedPdf = await PDFDocument.create()

      if(shownum === 2 || shownum === 3) {
        await appendCardPagePDFs(mergedPdf, exportItems)
      }else if(shownum === 1) {
        await appendStyleOnePagedPDFs(mergedPdf, exportItems)
      }else {
        const chunkSize = 40
        const batchCount = Math.ceil(children.length / chunkSize)

        for(let i = 0; i < children.length; i += chunkSize) {
          tempContainer = document.createElement('div')
          tempContainer.className = styles['products']
          tempContainer.style.position = 'fixed'
          tempContainer.style.left = '-10000px'
          tempContainer.style.top = '0'
          tempContainer.style.backgroundColor = '#FFFFFF'

          if(i === 0) {
            const logoElement = document.createElement('img')
            logoElement.src = logo
            logoElement.style.height = '60px'
            logoElement.className = styles['page-logo']
            tempContainer.appendChild(logoElement)
            tempContainer.style.paddingTop = '170px'

            const titleElement = document.createElement('div')
            titleElement.innerText = titleText
            titleElement.className = styles['sp']
            tempContainer.appendChild(titleElement)
          }

          for(const child of children.slice(i, i + chunkSize)) {
            tempContainer.appendChild(child.cloneNode(true))
          }
          document.body.appendChild(tempContainer)
          const currentContainer = tempContainer
          cleanupTempContainer = registerCleanup(() => currentContainer.remove())
          await waitForContainerAssets(currentContainer)
          const pdfData = await containerToPdfData(currentContainer)
          await appendPdfData(mergedPdf, pdfData)
          const batchIndex = Math.floor(i / chunkSize) + 1
          const processed = Math.min(batchIndex * chunkSize, exportTotal)
          updateExportProgress(Math.round((batchIndex / batchCount) * 90), processed)
          cleanupTempContainer()
          cleanupTempContainer = null
          tempContainer = null
          await yieldToBrowser(EXPORT_INTERACTIVE_PAUSE)
          ensureExportActive()
        }
      }

      updateExportProgress(95, exportTotal)
      let mergedPdfBytes: Uint8Array | null = await mergedPdf.save({ useObjectStreams: true })
      ensureExportActive()
      objectUrl = URL.createObjectURL(new Blob([mergedPdfBytes], { type: 'application/pdf' }))
      mergedPdfBytes = null
      objectUrlsRef.current.add(objectUrl)
      updateExportProgress(100, exportTotal)
      downloadFile(objectUrl, options.filename, options.showSuccessMessage !== false)
      return true
    }catch(error) {
      if((error as Error).message !== 'PDF_EXPORT_CANCELLED') {
        console.error('PDF export failed', error)
        message.error(intl.formatMessage({ id: '导出失败' }))
      }
      return false
    }finally {
      cleanupTempContainer?.()
      tempContainer?.remove()
      exportProgressHandlerRef.current = null
      if(!cancelledRef.current && !options.preserveProgressState) {
        setLoading(false)
        setProgress(0)
        setProcessedCount(0)
        setTotalCount(0)
      }
      if(objectUrl) {
        const revokeObjectUrl = () => {
          URL.revokeObjectURL(objectUrl)
          objectUrlsRef.current.delete(objectUrl)
        }
        const revokeDelay = options.objectUrlRevokeDelay ?? PDF_URL_REVOKE_DELAY
        if(options.waitForObjectUrlRevoke) {
          await new Promise<void>((resolve) => {
            window.setTimeout(() => {
              revokeObjectUrl()
              resolve()
            }, revokeDelay)
          })
          await waitForBrowserMemorySettle()
        }else {
          window.setTimeout(revokeObjectUrl, revokeDelay)
        }
      }
    }
  }

  const handleExportClick = () => {
    if(!cardItems.length) {
      message.warning(intl.formatMessage({ id: '请先搜索导出数据' }))
      return
    }

    setExportMode(ishm && shownum === 2 ? 'multiple' : 'single')
    setExportFiles(buildExportFiles(cardItems))
    setMultiExportCompleted(false)
    setExportModeOpen(true)
  }

  const handleExportModeConfirm = () => {
    if(exportMode === 'single') {
      setExportModeOpen(false)
      generatePDF('pdfContent', {
        filename: getSingleExportFileName(title),
        forceMergedCardExport: true,
        waitForObjectUrlRevoke: true,
        objectUrlRevokeDelay: SINGLE_PDF_URL_REVOKE_DELAY,
      })
      return
    }

    const selectedFiles = exportFiles.filter((file) => file.checked)
    if(!selectedFiles.length) {
      message.warning(intl.formatMessage({ id: '请选择需要导出的PDF文件' }))
      return
    }

    setExportModeOpen(false)
    setExportFiles(selectedFiles.map((file) => ({
      ...file,
      progress: 0,
      status: 'waiting',
    })))
    setExportProgressOpen(true)
    setMultiExportCompleted(false)
    exportStartTimerRef.current = window.setTimeout(() => {
      exportStartTimerRef.current = null
      if(!mountedRef.current || multiCancelledRef.current) return
      exportMultiplePDFs(selectedFiles)
    })
  }

  const exportMultiplePDFs = async (files: ExportFileItem[]) => {
    if(!mountedRef.current) return
    multiCancelledRef.current = false
    lastExportProgressUpdateRef.current = 0
    setMultiExporting(true)
    setProgress(1)
    setProcessedCount(0)
    setTotalCount(files.length)
    let allSuccess = true

    for(let i = 0; i < files.length; i++) {
      const file = files[i]
      if(multiCancelledRef.current) {
        updateExportFile(file.id, { status: 'terminated', progress: 0 })
        continue
      }

      if(!mountedRef.current) return
      updateExportFile(file.id, { status: 'exporting', progress: 1 })
      exportProgressHandlerRef.current = (percent) => {
        if(!mountedRef.current) return
        const now = Date.now()
        if(percent < 100 && now - lastExportProgressUpdateRef.current < EXPORT_PROGRESS_UPDATE_INTERVAL) return
        lastExportProgressUpdateRef.current = now
        updateExportFile(file.id, { progress: percent })
        const totalPercent = Math.floor(((i + percent / 100) / files.length) * 100)
        setProgress(Math.min(totalPercent, 99))
        setProcessedCount(i)
      }

      const success = await generatePDF('pdfContent', {
        items: cardItems.slice(file.start, file.end),
        filename: file.filename,
        useIndependentCardExport: true,
        showSuccessMessage: false,
        preserveProgressState: true,
        waitForObjectUrlRevoke: true,
        objectUrlRevokeDelay: MULTI_PDF_URL_REVOKE_DELAY,
      })

      exportProgressHandlerRef.current = null
      if(!mountedRef.current) return

      if(multiCancelledRef.current || !success) {
        allSuccess = false
        updateExportFile(file.id, {
          status: multiCancelledRef.current ? 'terminated' : 'failed',
          progress: multiCancelledRef.current ? 0 : 100,
        })
        if(!success) break
      }else {
        updateExportFile(file.id, { status: 'completed', progress: 100 })
        setProcessedCount(i + 1)
      }
    }

    const completed = !multiCancelledRef.current && allSuccess
    if(!mountedRef.current) return
    setMultiExporting(false)
    setMultiExportCompleted(completed)
    setProgress(completed ? 100 : 0)
    setProcessedCount(completed ? files.length : 0)
    setLoading(false)
    if(completed) cancelledRef.current = false
    if(completed) message.success(intl.formatMessage({ id: '已全部导出成功' }))
  }

  const closeExportProgressModal = () => {
    if(multiExporting && !multiExportCompleted) {
      Modal.confirm({
        title: intl.formatMessage({ id: '确认终止导出？' }),
        content: intl.formatMessage({ id: '关闭后会终止后续PDF导出，已下载成功的文件会保留。' }),
        okText: intl.formatMessage({ id: '确认' }),
        cancelText: intl.formatMessage({ id: '取消' }),
        onOk: () => {
          multiCancelledRef.current = true
          cancelledRef.current = true
          if(exportStartTimerRef.current !== null) {
            window.clearTimeout(exportStartTimerRef.current)
            exportStartTimerRef.current = null
          }
          exportProgressHandlerRef.current = null
          revokeManagedObjectUrls()
          setExportFiles([])
          setMultiExporting(false)
          setExportProgressOpen(false)
          setLoading(false)
        },
      })
      return
    }

    setExportProgressOpen(false)
    setExportFiles([])
  }

  const getExportFileStatusText = (status: ExportFileStatus) => {
    const statusMap: Record<ExportFileStatus, string> = {
      waiting: intl.formatMessage({ id: '等待中' }),
      exporting: intl.formatMessage({ id: '导出中' }),
      completed: intl.formatMessage({ id: '已完成' }),
      failed: intl.formatMessage({ id: '失败' }),
      terminated: intl.formatMessage({ id: '已终止' }),
    }
    return statusMap[status]
  }

  return (
    <>
      <Modal
        title={
          <div className="flex-b-c">
            <div className="flex-s-c">
              <label style={{ fontSize: 16 }}>{intl.formatMessage({ id: '预览' })}</label>
              <span>({intl.formatMessage({ id: '导出时保持处于当前页面' })})</span>
            </div>
            <div className="flex-s-c">
              {loading ? (
                <>
                  <span className={styles['export-count']}>{processedCount}/{totalCount}</span>
                  <Progress type="circle" percent={progress} size={36} />
                </>
              ) : null}
              <Button type="primary" className="mr30 ml10" loading={loading} onClick={handleExportClick}>{intl.formatMessage({ id: '导出' })}</Button>
            </div>
          </div>
        }
        open={true}
        onCancel={() => setOpen(false)}
        destroyOnClose
        footer={null}
        width={1100}
        maskClosable={false}
        
      >
      {usePaginatedPreview ? (
        <Alert
          className="mb15"
          type="info"
          showIcon
          message={intl.formatMessage(
            { id: '共{n}条数据，预览已启用分页。' },
            { n: list.length },
          )}
        />
      ) : null}
      <div
        className={`${styles['pdfcon']} ${usePaginatedPreview ? styles['preview-scroll'] : ''}`}
        id="pdfcon"
      >
        {
          shownum === 3 ? (
            <>
              <div className={styles['page-logo-1']} style={{ left: 20 }}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div  style={{ marginTop: 100 }}>
                <div className="flex-s-c">
                  <div className="plr15 ptb5" style={{ background: 'black', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                    {titleText}
                  </div>
                </div>
                <div id="pdfContent" className={styles['products']} style={{ marginTop: 20, width: 1000 }}>
                  {previewItems.map(renderStyleThreeCard)}
                </div>
              </div>
            </>
          ) : shownum === 2 ? (
            <>
              <div className={styles['page-logo-1']} style={{ left: 20 }}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div  style={{ marginTop: 100 }}>
                <div className="flex-s-c">
                  <div className="plr15 ptb5" style={{ background: '#1890ff', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                    {titleText}
                  </div>
                </div>
                <div id="pdfContent" className={styles['products']} style={{ marginTop: 20, width: 1000 }}>
                  {previewItems.map(renderStyleTwoCard)}
                </div>
              </div>
              
            </>
          ): (
            <>
              <div className={styles['page-logo']}>
                <img alt="" style={{ height: 60 }} src={logo} />
              </div>
              <div className="flex-s-c" style={{ paddingTop: 50 }}>
                <div className="plr15 ptb5" style={{ background: '#1890ff', color: '#fff', borderRadius: 8, fontSize: 18, fontWeight: 500 }}>
                  {titleText}
                </div>
              </div>
              <div className={`${styles['products']} ${styles['products-style-one']}`} id="pdfContent">
                {previewItems.map(renderStyleOneCard)}
              </div>
            </>
          )
        }

        
      </div>
      {usePaginatedPreview ? (
        <div className={styles['preview-pagination']}>
          <Pagination
            current={previewPage}
            pageSize={PREVIEW_PAGE_SIZE}
            total={cardItems.length}
            showSizeChanger={false}
            showTotal={(total: number) => intl.formatMessage({id: '共{n}条'}, {n: total})}
            onChange={(page) => {
              setPreviewPage(page)
              document.getElementById('pdfcon')?.scrollTo({ top: 0 })
            }}
          />
        </div>
      ) : null}
      </Modal>
      <Modal
        title={intl.formatMessage({ id: '导出方式选择' })}
        open={exportModeOpen}
        onCancel={() => setExportModeOpen(false)}
        onOk={handleExportModeConfirm}
        okText={intl.formatMessage({ id: '确定' })}
        cancelText={intl.formatMessage({ id: '取消' })}
        width={710}
        maskClosable={false}
      >
        <div className={styles['export-mode-row']}>
          <span className={styles['export-mode-label']}>{intl.formatMessage({ id: '导出方式' })}:</span>
          <Checkbox checked={exportMode === 'single'} onChange={() => setExportMode('single')}>
            {intl.formatMessage({ id: '一份pdf(不可编辑商品)' })}
          </Checkbox>
          <Checkbox checked={exportMode === 'multiple'} onChange={() => setExportMode('multiple')}>
            {intl.formatMessage({ id: '多份pdf(可编辑商品)' })}
          </Checkbox>
        </div>
        <div className={styles['export-file-list']}>
          {exportMode === 'single' ? (
            <div className={styles['export-file-item']}>
              <Checkbox checked disabled />
              <span className={styles['pdf-icon']}>PDF</span>
              <span>{getSingleExportFileName(title)}</span>
            </div>
          ) : (
            <>
              <div className={styles['export-file-toolbar']}>
                <Checkbox
                  checked={allExportFilesChecked}
                  indeterminate={someExportFilesChecked}
                  onChange={(event) => toggleAllExportFiles(event.target.checked)}
                >
                  {allExportFilesChecked ? intl.formatMessage({ id: '取消全选' }) : intl.formatMessage({ id: '全选' })}
                </Checkbox>
                <span>{checkedExportFileCount}/{exportFiles.length}</span>
              </div>
              {exportFiles.map((file) => (
                <div className={styles['export-file-item']} key={file.id}>
                  <Checkbox
                    checked={file.checked}
                    onChange={(event) => updateExportFile(file.id, { checked: event.target.checked })}
                  />
                  <span className={styles['pdf-icon']}>PDF</span>
                  <span>{file.filename}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </Modal>
      <Modal
        title={`${intl.formatMessage({ id: 'PDF导出进度' })}(${intl.formatMessage({ id: '导出时保持处于当前页面' })})`}
        open={exportProgressOpen}
        onCancel={closeExportProgressModal}
        footer={[
          <Button key="close" onClick={closeExportProgressModal}>
            {intl.formatMessage({ id: '关闭' })}
          </Button>,
        ]}
        width={760}
        maskClosable={false}
      >
        {multiExportCompleted ? (
          <Alert className="mb15" type="success" showIcon message={intl.formatMessage({ id: '已全部导出成功' })} />
        ) : null}
        <div className={styles['export-progress-summary']}>
          <span>{intl.formatMessage({ id: '整体进度' })}</span>
          <Progress percent={progress} />
        </div>
        <div className={styles['export-progress-list']}>
          {exportFiles.map((file) => (
            <div className={styles['export-progress-item']} key={file.id}>
              <div className={styles['export-progress-file']}>
                <span className={styles['pdf-icon']}>PDF</span>
                <span>{file.filename}</span>
                <span className={styles[`export-status-${file.status}`]}>{getExportFileStatusText(file.status)}</span>
              </div>
              <Progress percent={file.progress} size="small" />
            </div>
          ))}
        </div>
      </Modal>
    </>
    
  )
}

export default Pdfcon
